/**
 * Mockups API
 *
 * API methods for AI mockup generation operations.
 */

import { api } from './client';

// ============================================
// Types
// ============================================

export interface AIMockupSettings {
  refinementPromptTemplate: string;  // Stage 2: Global prompt for patch-on-product placement
}

export interface PatchTemplate {
  id: string;
  name: string;
  storagePath: string;
  prompt: string;  // Stage 1: Per-template prompt for logo-on-patch generation
  previewUrl: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  isActive: boolean;
}

export interface TemplateUpdateData {
  name?: string;
  prompt?: string;
  storagePath?: string;
}

export interface GenerateMockupResponse {
  jobId: string;
}

export interface MockupJobStatus {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: string | null;
  result: {
    proofFileId: string;
    downloadUrl: string;
    storagePath: string;
  } | null;
  error: string | null;
  billingError: 'insufficientBalance' | 'powerPackDisabled' | null;
  billingInfo: {
    imageBalance?: number;
    creditsAvailable?: number;
  } | null;
}

export interface BillingBalance {
  imageBalance: number;
  totalImagesUsed: number;
  creditsConsumed: number;
  creditsAvailable: number;
  totalImageCapacity: number;
  linkedEmail: string;
}

export interface TemplateUploadResponse {
  uploadUrl: string;
  storagePath: string;
}

export interface TemplateConfirmResponse {
  success: boolean;
  template?: PatchTemplate;
  error?: string;
}

// ============================================
// AI Mockup Generation
// ============================================

/**
 * Create an async AI mockup generation job
 * Returns a jobId — poll getMockupJobStatus() for progress/result
 */
export async function generateAIMockup(
  orderId: string,
  customerArtworkFileId: string,
  productImageUrl: string,
  templateId: string
): Promise<GenerateMockupResponse> {
  return api.post<GenerateMockupResponse>('/mockups/generate', {
    orderId,
    customerArtworkFileId,
    productImageUrl,
    templateId,
  });
}

/**
 * Poll the status of an async mockup generation job
 */
export async function getMockupJobStatus(jobId: string): Promise<MockupJobStatus> {
  return api.get<MockupJobStatus>(`/mockups/generate/${jobId}`);
}

// ============================================
// Settings
// ============================================

/**
 * Get AI mockup settings
 */
export async function getAIMockupSettings(): Promise<AIMockupSettings> {
  return api.get<AIMockupSettings>('/mockups/settings');
}

/**
 * Update AI mockup refinement prompt template (Stage 2)
 */
export async function updateAIMockupRefinementPrompt(
  refinementPromptTemplate: string
): Promise<{ success: boolean; error?: string }> {
  return api.put<{ success: boolean; error?: string }>('/mockups/settings', {
    refinementPromptTemplate,
  });
}

// ============================================
// Patch Templates
// ============================================

/**
 * Get all patch templates
 */
export async function getPatchTemplates(): Promise<{ templates: PatchTemplate[] }> {
  return api.get<{ templates: PatchTemplate[] }>('/mockups/templates');
}

/**
 * Get a single patch template by ID
 */
export async function getPatchTemplate(
  templateId: string
): Promise<{ template: PatchTemplate }> {
  return api.get<{ template: PatchTemplate }>(`/mockups/templates/${templateId}`);
}

/**
 * Get a signed URL to upload a patch template
 */
export async function getTemplateUploadUrl(
  filename: string,
  mimeType: string
): Promise<TemplateUploadResponse> {
  return api.post<TemplateUploadResponse>('/mockups/templates/upload', {
    filename,
    mimeType,
  });
}

/**
 * Confirm template upload and create template record
 */
export async function confirmTemplateUpload(
  name: string,
  storagePath: string,
  prompt?: string
): Promise<TemplateConfirmResponse> {
  return api.post<TemplateConfirmResponse>('/mockups/templates/confirm', {
    name,
    storagePath,
    prompt,
  });
}

/**
 * Upload a patch template file (convenience method)
 */
export async function uploadPatchTemplate(
  file: File,
  name: string,
  prompt?: string
): Promise<TemplateConfirmResponse> {
  // 1. Get signed upload URL
  const { uploadUrl, storagePath } = await getTemplateUploadUrl(file.name, file.type);

  // 2. Upload file directly to Cloud Storage
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    return {
      success: false,
      error: 'Failed to upload template file',
    };
  }

  // 3. Confirm upload and create template record with prompt
  return confirmTemplateUpload(name, storagePath, prompt);
}

/**
 * Update an existing patch template
 */
export async function updatePatchTemplate(
  templateId: string,
  updates: TemplateUpdateData
): Promise<TemplateConfirmResponse> {
  return api.put<TemplateConfirmResponse>(`/mockups/templates/${templateId}`, updates);
}

/**
 * Delete a patch template
 */
export async function deletePatchTemplate(
  templateId: string
): Promise<{ success: boolean; error?: string }> {
  return api.delete<{ success: boolean; error?: string }>(
    `/mockups/templates/${templateId}`
  );
}

// ============================================
// Billing
// ============================================

/**
 * Get the shop's AI image billing balance
 */
export async function getAIMockupBilling(): Promise<BillingBalance> {
  return api.get<BillingBalance>('/mockups/billing');
}
